// Modal — backdrop + dialog with header / body / footer slots.
//
// Sizes: sm (max-w-sm), md (max-w-md, default), lg (max-w-2xl).
// Backdrop: Ink @ 40% opacity (rgba via inline style sourced from
// colors.shadowInk's parent token) — paints the page behind the dialog.
// Footer is a flex row, right-aligned. Pass <Button>s (or anything) as
// children of the footer slot.
//
// Closes on:
//   • clicking the X icon in the header
//   • pressing ESC
//   • clicking the backdrop (not the dialog itself)
//
// Renders via React.createPortal to document.body so the dialog escapes
// any overflow:hidden ancestor (an empty document.body in SSR / first
// pass is tolerated — the portal mount only runs after `open` flips true).

import { X } from "lucide-react";
import {
  useCallback,
  useEffect,
  type MouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { colors } from "@/design-system/tokens";

import { cn } from "./cn";

// Backdrop: Ink (text-primary token) at 40% opacity. The shadowInk token
// is the same Ink at 6% — too faint for a backdrop. We compose a fresh
// rgba from the same source so the ink color stays canonical here.
function inkAtAlpha(alpha: number): string {
  // Parse the textPrimary hex (#0B0B0F = 11, 11, 15) once, return the
  // rgba() string. Keeps the literal hex out of the component body.
  const hex = colors.textPrimary.replace("#", "");
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export type ModalSize = "sm" | "md" | "lg";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  size?: ModalSize;
  children?: ReactNode;
  footer?: ReactNode;
  /** Override aria-label for the close button (defaults to "Close"). */
  closeLabel?: string;
  className?: string;
}

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-2xl",
};

export function Modal({
  open,
  onClose,
  title,
  size = "md",
  children,
  footer,
  closeLabel = "Close",
  className,
}: ModalProps) {
  // ESC key listener — only attach while open so we don't fight other
  // ESC consumers when the modal is hidden.
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onKeyDown]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  // Stop click-bubble from inside the dialog so the backdrop click
  // handler only fires on the actual backdrop.
  const onDialogClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="modal-backdrop"
      style={{ backgroundColor: inkAtAlpha(0.4) }}
    >
      <div
        data-size={size}
        onClick={onDialogClick}
        // ``max-h-[calc(100vh-2rem)]`` matches the outer ``p-4`` so the
        // dialog never bleeds past the viewport edges. ``flex-col`` +
        // ``min-h-0`` on the body lets the body scroll while the header
        // and footer stay pinned.
        // ``transition-[max-width]`` smooths size jumps when callers
        // promote the dialog mid-flow (e.g. New Commitment quick →
        // full create swap from ``sm`` to ``lg``).
        className={cn(
          "flex max-h-[calc(100vh-2rem)] w-full flex-col rounded-lg bg-raised shadow-overlay border border-border-subtle transition-[max-width] duration-slow ease-out",
          SIZE_CLASSES[size],
          className,
        )}
      >
        <header className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-border-subtle px-5 py-4">
          <h2 id="modal-title" className="text-md font-semibold text-text-primary">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-tertiary transition-fast ease-out hover:bg-recessed hover:text-text-primary"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        <div
          data-testid="modal-body"
          className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-base text-text-primary"
        >
          {children}
        </div>
        {footer && (
          <footer className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-border-subtle px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
